import React from 'react';
import styled from '@emotion/styled';
import { Box, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCurrentTableData } from '@/redux/selectors/dataTableSelectors';
import { ApiHourlyData } from '@/components/_common/Table/Calendar/CalendarComponents/types';
// import OverflowMenu from '@/components/_common/OverflowMenu/OverflowMenu';
// import OverflowMenuItem from '@/components/_common/OverflowMenu/OverflowMenuItem';
import NoResults from '@/components/_common/Table/Calendar/CalendarSharedComponents/NoResults';
import { LicenseChip } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import { getAvailBgColorByStatus, getAvailStatus, getColorByAvailStatus } from '@/components/_common/Table/Calendar/utils';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';
import FocusImage from '@/components/Focus/common/FocusImage';
import { setSelectedAvailId } from '@/redux/slices/focusCalendarSlice';
import { selectSelectedAvailId } from '@/redux/selectors/focusCalendarSelectors';

const DailyView = () => {
  const theme = useFocusTheme();
  const dispatch = useAppDispatch();
  const avails: ApiHourlyData[] = useAppSelector(selectCurrentTableData);
  const selectedAvailId = useAppSelector(selectSelectedAvailId);
  const { time_format } = useAppSelector(selectUserPreferences);

  if (avails.length <= 0) {
    return <NoResults />;
  }

  return (
    <Box p={4}>
      {avails.map((avail) => {
        const status = getAvailStatus(avail);
        const color = getColorByAvailStatus(status, theme.palette.mode === 'dark');
        const isSelected = avail.id === selectedAvailId;

        return (
          <AvailWrapper
            key={avail.id}
            isSelected={isSelected}
            backgroundColor={isSelected ? getAvailBgColorByStatus(status) : theme.customOverrides.Calendar.backgroundDefault}
            hoverColor={theme.customOverrides.Calendar.backgroundAlt}
            sx={{
              borderColor: isSelected ? getColorByAvailStatus(status, theme.palette.mode === 'dark') : 'transparent'
            }}
            onClick={() => dispatch(setSelectedAvailId(avail.id))}
          >
            <TimeText>{format(new Date(avail.term_start), time_format || 'hh:mm aa')}</TimeText>

            <ColorBar sx={{ backgroundColor: color }} />

            <ThumbnailPlaceholder>
              <FocusImage entity={avail} />
            </ThumbnailPlaceholder>

            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <CodeText>{avail.title.client_ids_supplemental.alpha_id}</CodeText>
              <DetailsText>
                {avail.title.title_name_supplemental}
                {!!avail.title.season_sequence_order && !!avail.title.episode_sequence_order && (
                  <>
                    {' '}
                    • S{avail.title.season_sequence_order} • E{avail.title.episode_sequence_order}
                  </>
                )}
              </DetailsText>
            </Stack>

            <LicenseChips>
              {avail.license_types.map((l) => (
                <LicenseChip key={l} label={l.toLocaleUpperCase()} />
              ))}
            </LicenseChips>
            {/* TODO: Removing for now, will be added back later */}
            {/* <OverflowMenu>
              <OverflowMenuItem>Item 1</OverflowMenuItem>
              <OverflowMenuItem>Item 2</OverflowMenuItem>
            </OverflowMenu> */}
          </AvailWrapper>
        );
      })}
    </Box>
  );
};

export default DailyView;

const AvailWrapper = styled(Box, { shouldForwardProp: (prop) => !['isSelected', 'backgroundColor', 'hoverColor'].includes(prop) })<{
  isSelected: boolean;
  backgroundColor: string;
  hoverColor: string;
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1.75)};
  border-width: 2px;
  border-style: solid;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  background-color: ${({ backgroundColor }) => backgroundColor};
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: ${({ isSelected, backgroundColor, hoverColor }) =>
      isSelected ? `color-mix(in srgb, ${backgroundColor} 100%, white 3%)` : `color-mix(in srgb, ${hoverColor} 100%, white 3%)`};
  }
`;

const TimeText = styled(Typography)`
  color: ${({ theme }) => theme.customOverrides.Calendar.text};
  font-size: 1.143rem;
  min-width: 80px;
`;

const ColorBar = styled(Box)`
  width: 4px;
  height: 43px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const ThumbnailPlaceholder = styled(Box)`
  width: 93px;
  aspect-ratio: 16/9;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  background-color: ${({ theme }) => theme.customOverrides.Calendar.hover};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.customOverrides.Calendar.textTertiary};
`;

const CodeText = styled(Typography)`
  min-width: 80px;
  color: ${({ theme }) => theme.customOverrides.Calendar.text};
  font-size: 1.143rem;
`;

const DetailsText = styled(Typography)`
  color: ${({ theme }) => theme.customOverrides.Calendar.textSecondary};
  font-size: 1rem;
`;

const LicenseChips = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
`;
